import { useEffect, useState } from "react";
import wrapperHOC from "@Utils/wrapperHOC";
import Image from "@Components/Image";
import styles from "../neighbourhoodSection.module.scss";

type HeroCardProps = {
  image: string;
  stat?: string;
  statSuffix?: string;
  label?: string;
  alt?: string;
};

// Tablet viewport band (inclusive) that should use the "-tablet" hero variant.
const TABLET_MIN_WIDTH = 479;
const TABLET_MAX_WIDTH = 899;

/**
 * Insert a "-tablet" suffix before the file extension of the hero image URL,
 * preserving any query string. e.g.
 *   .../climate.png?w=600&auto=format  →  .../climate-tablet.png?w=600&auto=format
 */
const toTabletImage = (url: string): string => {
  const [path, query] = url.split("?");
  const tabletPath = path.replace(/(\.[a-zA-Z0-9]+)$/, "-tablet$1");
  return query ? `${tabletPath}?${query}` : tabletPath;
};

const HeroCard = ({ image, stat, statSuffix, label, alt = "Neighbourhood" }: HeroCardProps) => {
  // Swap to the tablet variant between 479px and 899px. Guarded so SSR/first
  // paint uses the default image (no hydration mismatch); the effect updates it
  // on the client and on resize.
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const check = () => {
      const w = window.innerWidth;
      setIsTablet(w >= TABLET_MIN_WIDTH && w <= TABLET_MAX_WIDTH);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const resolvedImage = isTablet ? toTabletImage(image) : image;

  return (
    <div className={styles.heroCard}>
      <Image
        src={resolvedImage}
        alt={alt}
        className={styles.heroImage}
        width="100%"
        height="100%"
        isEagerLoad
      />
      {stat && (
        <>
          <div className={styles.heroOverlay} />
          <div className={styles.heroContent}>
            <p className={styles.heroStat}>
              <span className={styles.heroStatLarge}>{stat}</span>
              {statSuffix && (
                <>
                  {" "}
                  <span className={styles.heroStatSmall}>{statSuffix}</span>
                </>
              )}
            </p>
            {label && <p className={styles.heroLabel}>{label}</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default wrapperHOC(HeroCard, {
  componentName: "HeroCard",
  showForChina: false,
  showForWhiteLable: false,
});
